// Phase 14k-126: AI chat assistant 右下角 floating button + drawer
// 只 Pro+ tier 且已绑 BYO LLM key 才显示

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Fab, Drawer, IconButton, Typography, TextField, Button,
  CircularProgress, Chip, Link as MuiLink, Stack,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Link as RouterLink } from 'react-router-dom';
import { palette, typo } from '../theme';
import LockedOverlay from './LockedOverlay';

const ROBOT_SVG = '/chat-robot.svg';

// 把 AI 回答里的 [文字](/path) 转成 RouterLink 单击跳产品页
function renderWithLinks(text) {
  if (!text) return null;
  const parts = [];
  const regex = /\[([^\]]+)\]\(([^)]+)\)/g;
  let lastIdx = 0;
  let match;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIdx) {
      parts.push({ kind: 'text', value: text.slice(lastIdx, match.index) });
    }
    parts.push({ kind: 'link', label: match[1], href: match[2] });
    lastIdx = regex.lastIndex;
  }
  if (lastIdx < text.length) {
    parts.push({ kind: 'text', value: text.slice(lastIdx) });
  }
  return parts.map((p, i) => {
    if (p.kind === 'text') {
      // 保留换行
      return (
        <span key={i}>
          {p.value.split('\n').map((line, j, arr) => (
            <React.Fragment key={j}>
              {line}
              {j < arr.length - 1 && <br />}
            </React.Fragment>
          ))}
        </span>
      );
    }
    // link: 站内 / 部分 path
    if (p.href.startsWith('/')) {
      return (
        <MuiLink key={i} component={RouterLink} to={p.href}
          sx={{ color: palette.ai, fontWeight: 600, textDecoration: 'underline dotted', '&:hover': { color: palette.accentBright } }}>
          {p.label}
        </MuiLink>
      );
    }
    return (
      <MuiLink key={i} href={p.href} target="_blank" rel="noopener noreferrer"
        sx={{ color: palette.ai, fontWeight: 600 }}>
        {p.label}
      </MuiLink>
    );
  });
}

export default function AiChatFloat() {
  const [eligible, setEligible] = useState(false);
  const [quota, setQuota] = useState({ used: 0, limit: 0, remaining: 0 });
  const [tier, setTier] = useState(null);
  const [hasKey, setHasKey] = useState(false);
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);  // {role: 'user'|'ai', text, filtered?: bool}
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const listRef = useRef(null);

  // 拉 quota / eligible 状态
  const refresh = useCallback(async () => {
    try {
      const r = await fetch('/api/me/ai-chat/quota');
      if (!r.ok) {
        setEligible(false);
        return;
      }
      const d = await r.json();
      setEligible(!!d.eligible);
      setQuota({ used: d.used || 0, limit: d.limit || 0, remaining: d.remaining || 0 });
      setTier(d.tier);
      setHasKey(!!d.has_byo_key);
    } catch {
      setEligible(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    // 不轮询 — 用户开抽屉时再 refresh
  }, [refresh]);

  // 客服闭环(2026-06-11): 窗口打开时每20秒拉一次人工回复, 合并成「人工客服」气泡
  const lastAdminId = useRef(0);
  useEffect(() => {
    if (!open) return undefined;
    const pull = async () => {
      try {
        const r = await fetch(`/api/me/support-thread?since=${lastAdminId.current}`);
        if (!r.ok) return;
        const d = await r.json();
        const rows = d.messages || [];
        if (rows.length) {
          lastAdminId.current = rows[rows.length - 1].id;
          setMessages(prev => [...prev, ...rows.map(m => ({ role: 'admin', text: m.text }))]);
        }
      } catch { /* */ }
    };
    pull();
    const t = setInterval(pull, 20000);
    return () => clearInterval(t);
  }, [open]);

  useEffect(() => {
    if (open) {
      refresh();
      // 滚到最新
      setTimeout(() => {
        if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
      }, 100);
    }
  }, [open, messages.length, refresh]);

  const send = async () => {
    const msg = input.trim();
    if (!msg || sending) return;
    setMessages(prev => [...prev, { role: 'user', text: msg }]);
    setInput('');
    setSending(true);
    // 非eligible(免费/未绑key) → 纯人工客服通道
    if (!eligible) {
      try {
        const r = await fetch('/api/me/support-message', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ message: msg }),
        });
        setMessages(prev => [...prev, r.ok
          ? { role: 'admin', text: '消息已送达人工客服 — 回复会直接出现在这个窗口, 也可以稍后回来查看。' }
          : { role: 'ai', text: '⚠️ 发送失败, 请稍后再试或邮件联系。', isError: true }]);
      } catch {
        setMessages(prev => [...prev, { role: 'ai', text: '⚠️ 网络出错, 稍后再试', isError: true }]);
      } finally {
        setSending(false);
      }
      return;
    }
    try {
      const r = await fetch('/api/me/ai-chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: msg }),
      });
      const d = await r.json();
      if (r.ok && d.ok) {
        setMessages(prev => [...prev, { role: 'ai', text: d.text, filtered: d.filtered }]);
        if (d.quota) setQuota(d.quota);
      } else {
        const err = d.error || d.text || '出了点问题, 稍后再试';
        setMessages(prev => [...prev, { role: 'ai', text: `⚠️ ${err}`, isError: true }]);
        if (r.status === 429) refresh();
      }
    } catch (e) {
      setMessages(prev => [...prev, { role: 'ai', text: '⚠️ 网络出错, 稍后再试', isError: true }]);
    } finally {
      setSending(false);
    }
  };

  // 2026-06-11: 不再对非eligible隐藏 — 免费/未绑key用户走纯人工客服通道(售前答疑是最强转化场景)

  const drawerWidth = { xs: '100vw', sm: 420 };

  return (
    <>
      {/* Floating button — 右下角 */}
      <Fab
        onClick={() => setOpen(true)}
        sx={{
          position: 'fixed',
          bottom: { xs: 16, sm: 24 },
          right: { xs: 16, sm: 24 },
          width: 60, height: 60,
          background: `radial-gradient(circle at 35% 35%, ${palette.accentBright}, ${palette.ai} 55%, #7c3aed 100%)`,
          boxShadow: `0 4px 20px ${palette.accentGlow}, 0 0 0 1px rgba(255,255,255,0.1) inset`,
          transition: 'transform 220ms, box-shadow 220ms',
          zIndex: 1200,
          '&:hover': {
            transform: 'scale(1.06)',
            boxShadow: `0 6px 28px ${palette.accentGlow}, 0 0 0 1px rgba(255,255,255,0.15) inset`,
          },
        }}
        aria-label="AI 助手"
      >
        <Box component="img" src={ROBOT_SVG} alt="" sx={{ width: 42, height: 42 }} />
      </Fab>

      {/* Drawer 抽屉 */}
      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{
          sx: {
            width: drawerWidth,
            maxWidth: '100vw',
            bgcolor: palette.bg,
            color: palette.text,
            borderLeft: `1px solid ${palette.border}`,
          },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Header */}
          <Box sx={{
            px: 2, py: 1.5,
            borderBottom: `1px solid ${palette.border}`,
            display: 'flex', alignItems: 'center', gap: 1.5,
          }}>
            <Box component="img" src={ROBOT_SVG} alt="" sx={{ width: 36, height: 36 }} />
            <Box sx={{ flex: 1, minWidth: { xs: 0, sm: 200 } }}>
              <Typography sx={{ fontWeight: 700, fontSize: 15, color: palette.text, lineHeight: 1.2 }}>
                AI 量化助手
              </Typography>
              <Typography sx={{ fontSize: 11, color: palette.textMuted, fontFamily: typo.mono }}>
                {tier?.toUpperCase()} · {quota.remaining}/{quota.limit} 今日剩余
              </Typography>
            </Box>
            <IconButton size="small" onClick={refresh} sx={{ color: palette.textMuted }}>
              <RefreshIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={() => setOpen(false)} sx={{ color: palette.textMuted }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Messages list */}
          <Box ref={listRef} sx={{
            flex: 1,
            overflowY: 'auto',
            px: 2, py: 2,
            display: 'flex', flexDirection: 'column', gap: 1.5,
          }}>
            {messages.length === 0 && (
              <Box sx={{ py: 2, color: palette.textMuted }}>
                {/* 示例 AI 回复气泡（非eligible时展示 demo，eligible时展示引导） */}
                {!eligible && (
                  <Box sx={{
                    alignSelf: 'flex-start', mb: 1.5,
                    px: 1.5, py: 1.25, borderRadius: 1.5,
                    bgcolor: palette.surface,
                    border: `1px solid ${palette.border}`,
                    color: palette.text, fontSize: 13.5, lineHeight: 1.65,
                  }}>
                    <Typography sx={{ fontSize: 10, fontWeight: 700, color: palette.ai, mb: 0.5, letterSpacing: 0.5 }}>
                      🤖 AI 量化助手 (示例)
                    </Typography>
                    <Typography sx={{ fontSize: 13.5, lineHeight: 1.65 }}>
                      👋 你好！我是量化驾驶舱 AI 助手。<br/>
                      我可以帮你分析持仓、解释 EV 指标、排查 0 trades 异常等。<br/><br/>
                      <b>示例回答</b>: 策略 #29 的 leverage 被 AI 经理调低，是因为过去 7 天该市场波动率上升 23%，按风控信封自动收紧了仓位。你可以在「决策台」查看详细调整日志。
                    </Typography>
                  </Box>
                )}
                <Box sx={{ textAlign: 'center' }}>
                  <Box component="img" src={ROBOT_SVG} alt="" sx={{ width: 48, height: 48, opacity: 0.5, mb: 1 }} />
                  <Typography sx={{ fontSize: 13, lineHeight: 1.7, maxWidth: 320, mx: 'auto' }}>
                    {eligible
                      ? <>我是你的量化驾驶舱助手 (read-only)<br/>可以问我:</>
                      : <>订阅 Pro 并绑定 LLM key 解锁 AI 即时问答。<br/>现在也可以直接留言，<b>人工客服</b>会在此回复。</>}
                  </Typography>
                  <Stack spacing={0.75} sx={{ mt: 1.5 }}>
                    {[
                      '为什么 AI 调整了 #29 leverage?',
                      'EV 是什么意思? 我的 #61 EV 健康吗?',
                      '怎么手动 pause 一个策略?',
                      '我的资金应该再开新策略吗?',
                    ].map((q, i) => (
                      <Chip key={i} label={q} size="small" onClick={() => setInput(q)}
                        sx={{
                          bgcolor: 'rgba(167,139,250,0.06)',
                          border: `1px solid ${palette.border}`,
                          color: palette.text, fontSize: 11.5,
                          '&:hover': { borderColor: palette.borderAccent, bgcolor: 'rgba(167,139,250,0.12)' },
                        }}/>
                    ))}
                  </Stack>
                </Box>
              </Box>
            )}
            {messages.map((m, i) => (
              <Box key={i} sx={{
                alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
                maxWidth: '85%',
                px: 1.5, py: 1.25,
                borderRadius: 1.5,
                bgcolor: m.role === 'user'
                  ? 'rgba(167,139,250,0.12)'
                  : m.role === 'admin' ? 'rgba(0,212,170,0.08)'
                  : (m.isError ? 'rgba(239,68,68,0.08)' : palette.surface),
                border: `1px solid ${m.role === 'user' ? palette.borderAccent : m.role === 'admin' ? 'rgba(0,212,170,0.35)' : palette.border}`,
                color: palette.text,
                fontSize: 13.5,
                lineHeight: 1.65,
              }}>
                {m.role === 'admin' && (
                  <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#00d4aa', mb: 0.5, letterSpacing: 0.5 }}>
                    👤 人工客服
                  </Typography>
                )}
                {m.role === 'ai' ? renderWithLinks(m.text) : m.text}
                {m.filtered && (
                  <Typography sx={{ mt: 1, fontSize: 10, color: palette.textMuted, fontStyle: 'italic' }}>
                    (此回答经安全过滤)
                  </Typography>
                )}
              </Box>
            ))}
            {sending && (
              <Box sx={{ alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: 1, color: palette.textMuted, fontSize: 12 }}>
                <CircularProgress size={14} sx={{ color: palette.ai }} />
                思考中...
              </Box>
            )}
          </Box>

          {/* Input */}
          <Box sx={{
            p: 1.5,
            borderTop: `1px solid ${palette.border}`,
            display: 'flex', gap: 1, alignItems: 'flex-end',
          }}>
            <TextField
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              placeholder={eligible ? "问我策略/持仓; 含「归因/复盘/为什么亏」会自动深度调查..." : "留言给人工客服..."}
              multiline
              maxRows={4}
              fullWidth
              size="small"
              disabled={sending || quota.remaining <= 0}
              sx={{
                '& .MuiInputBase-root': {
                  bgcolor: palette.surface,
                  color: palette.text,
                  fontSize: 13.5,
                },
                '& fieldset': { borderColor: palette.border },
              }}
            />
            <Button
              onClick={send}
              disabled={!input.trim() || sending || quota.remaining <= 0}
              variant="contained"
              sx={{
                bgcolor: palette.ai, color: palette.bg,
                minWidth: 0, p: 1.25,
                '&:hover': { bgcolor: palette.accentBright },
                '&:disabled': { bgcolor: palette.border, color: palette.textMuted },
              }}
            >
              <SendIcon fontSize="small" />
            </Button>
          </Box>
          {quota.remaining <= 0 && (
            <Typography sx={{ fontSize: 11, color: palette.textMuted, textAlign: 'center', pb: 1.5 }}>
              今日 {quota.limit} 次额度用完, 明日 UTC 00:00 重置
            </Typography>
          )}
        </Box>
      </Drawer>
    </>
  );
}
